
// observer: BodySpec's first-party analytics + session replay (replaces
// Smartlook). Ingest lives in bodyspec/observer; the wire protocol is in its
// docs/protocol. Disabled unless GATSBY_OBSERVER_KEY is set at build time.
//
// Privacy: nothing is sent when the visitor has opted out (privacy-opt-out
// cookie or Global Privacy Control). Replay masks every input, never records
// report/account/payment pages (see replay.ts BUILTIN_EXCLUDED), and is off
// inside the app WebView.

import {isAppWebView} from 'utils/appWebview';
import {cookies, CookKey} from 'utils/cookies';
import {hasWindow} from 'utils/functions';
import {getLocalId} from 'utils/localId';
import {readOptOut} from 'utils/privacy';

import {firstTouch, readTouch, referralCode, Touch} from './attribution';
import {AutoCapture} from './autocapture';
import {loadConfig} from './config';
import {Replay, replaySupported} from './replay';
import {endSession, isCurrent, resolveSession, Session, touchSession} from './session';
import {Transport} from './transport';
import {SessionContext} from './types';
import {normalizePath, truncate, unitHash, uuid} from './util';

const KEY = process.env.GATSBY_OBSERVER_KEY || '';
const ENDPOINT = (process.env.GATSBY_OBSERVER_ENDPOINT || '/o').replace(/\/$/, '');

type State = {
	anonId: string
	userId?: string
	internal: boolean
	session: Session
	// Attribution for a session that started on this page load; sent until
	// the server has it (first non-null wins there)
	touch?: Touch
	parentSessionId?: string
	path: string
	// endPage already ran in beforeRoute for the navigation in progress
	pageEnded: boolean
	transport: Transport
	capture: AutoCapture
	replay: Replay | null
	replayRate: number
};

let state: State | null = null;

export {normalizeTarget} from './util';

const browserInfo = (): {browser?: string, os?: string, device: string} => {
	const ua = navigator.userAgent;
	const browser = /Edg\//.test(ua) ? 'Edge'
		: /Chrome\//.test(ua) ? 'Chrome'
			: /Firefox\//.test(ua) ? 'Firefox'
				: /Safari\//.test(ua) ? 'Safari' : undefined;
	const os = /iPhone|iPad|iPod/.test(ua) ? 'iOS'
		: /Android/.test(ua) ? 'Android'
			: /Mac OS X/.test(ua) ? 'macOS'
				: /Windows/.test(ua) ? 'Windows'
					: /Linux/.test(ua) ? 'Linux' : undefined;
	const device = window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop';
	return {browser, os, device};
};

const context = (): SessionContext => {
	const s = state as State;
	const t = s.touch;
	return {
		id: s.session.id,
		anon_id: s.anonId,
		user_id: s.userId,
		started_at: s.session.started,
		platform: 'web',
		viewport_w: window.innerWidth,
		viewport_h: window.innerHeight,
		is_internal: s.internal,
		parent_session_id: s.parentSessionId,
		...browserInfo(),
		...(!t ? {} : {
			entry_url: truncate(window.location.origin + t.landing, 2048),
			referrer: t.referrer,
			utm_source: t.utm_source,
			utm_medium: t.utm_medium,
			utm_campaign: t.utm_campaign,
			utm_term: t.utm_term,
			utm_content: t.utm_content,
			click_id: t.click_id,
			click_id_type: t.click_id_type,
			referral_code: referralCode(),
		}),
	};
};

let stopScheduled = false;

const enqueue = (name: string, props?: Record<string, unknown>) => {
	if (!state) {
		return;
	}
	if (readOptOut()) {
		// Drop the event, and shut down after the current call stack: the
		// caller (a route hook, endPage, ensureSession) may still touch
		// `state`, so it must not become null under it
		if (!stopScheduled) {
			stopScheduled = true;
			queueMicrotask(() => {
				stopScheduled = false;
				obs.stop();
			});
		}
		return;
	}
	// Context is captured now, not at send time, so events queued before a
	// logout still go out under the session and user they happened in
	state.transport.enqueue({
		id: uuid(),
		name: truncate(name, 128) || name,
		ts: Date.now(),
		path: state.path,
		...(props && Object.keys(props).length ? {props} : {}),
	}, context());
};

// The tracker must never throw into Gatsby's route hooks or a React render:
// Gatsby calls onPreRouteUpdate without a try/catch, and a throw there
// unmounts the app
const safe = <A extends unknown[]>(fn: (...args: A) => void) => (...args: A): void => {
	try {
		fn(...args);
	} catch {
		// analytics is best effort
	}
};

// Roll to a new session when the old one went idle or was reset
const ensureSession = () => {
	const s = state;
	if (!s) {
		return;
	}
	if (isCurrent(s.session)) {
		touchSession(s.session);
		return;
	}
	const touch = readTouch();
	const {session, isNew} = resolveSession(touch);
	s.session = session;
	if (isNew) {
		s.touch = touch;
		enqueue('$session_start', {first_touch: firstTouch(touch)});
	}
};

const syncReplay = () => {
	const s = state;
	if (!s?.replay) {
		return;
	}
	if (unitHash(s.session.id) >= s.replayRate) {
		s.replay.stop();
		return;
	}
	void s.replay.onPage(s.path, {
		endpoint: ENDPOINT,
		key: KEY,
		sessionId: s.session.id,
		anonId: s.anonId,
	});
};

const onHide = () => {
	if (!state) {
		return;
	}
	if (document.visibilityState === 'hidden') {
		state.transport.flushOnHide();
		void state.replay?.flush(true);
	}
};

export const obs = {
	get enabled(): boolean {
		return state !== null;
	},

	// gatsby-browser onClientEntry
	init: safe(() => {
		if (state || !KEY || !hasWindow() || readOptOut()) {
			return;
		}
		const embedded = isAppWebView();
		const touch = readTouch();
		const {session, isNew} = resolveSession(touch);
		const appAnon = embedded ? cookies.get(CookKey.ObserverAppAnon) : undefined;

		state = {
			// Inside the app, share the app's anon id so web + app join up
			anonId: truncate(appAnon, 64) || getLocalId(),
			internal: false,
			session,
			touch: isNew ? touch : undefined,
			parentSessionId: embedded ? cookies.get(CookKey.ObserverAppSession) : undefined,
			path: normalizePath(window.location.pathname),
			pageEnded: false,
			transport: new Transport(ENDPOINT, KEY),
			capture: new AutoCapture((name, props) => enqueue(name, props)),
			// No replay inside the app WebView (Apple tracking rules) or
			// without native gzip
			replay: embedded || !replaySupported() ? null : new Replay(),
			replayRate: 0,
		};
		if (isNew) {
			enqueue('$session_start', {first_touch: firstTouch(touch)});
		}
		state.capture.start();
		document.addEventListener('visibilitychange', onHide);
		window.addEventListener('pagehide', onHide);

		void loadConfig(ENDPOINT, KEY).then((config) => {
			if (!state) {
				return;
			}
			state.replayRate = config.replay_sample_rate;
			state.replay?.setExcluded(config.excluded_paths);
			syncReplay();
		}).catch(() => undefined);
	}),

	// gatsby-browser onPreRouteUpdate, before the next page renders: stop
	// replay ahead of an excluded page, and close out the current page while
	// its DOM (scroll height, size for $move) is still the one measured
	beforeRoute: safe((pathname: string) => {
		const s = state;
		if (!s) {
			return;
		}
		const path = normalizePath(pathname);
		// Stop replay first: this is the privacy-critical step, so nothing
		// below may get in its way
		if (path !== s.path && !s.pageEnded) {
			// Summary events for the page being left come before the pause
			// marker so they stay attributed to the recorded page
			s.capture.endPage();
			s.pageEnded = true;
		}
		s.replay?.beforeRoute(path);
	}),

	// gatsby-browser onRouteUpdate (fires for the first page too)
	page: safe((pathname: string) => {
		const s = state;
		if (!s) {
			return;
		}
		const path = normalizePath(pathname);
		if (path !== s.path && !s.pageEnded) {
			s.capture.endPage();
		}
		s.pageEnded = false;
		ensureSession();
		s.path = path;
		enqueue('$pageview', {title: truncate(document.title, 200)});
		syncReplay();
	}),

	track: safe((name: string, props?: Record<string, unknown>) => {
		if (state) {
			ensureSession();
			enqueue(name, props);
		}
	}),

	// Called on every render of usePermissions; only acts on a change
	identify: safe((userId: string, internal: boolean) => {
		if (!state || !userId || (state.userId === userId && state.internal === internal)) {
			return;
		}
		const changed = state.userId !== userId;
		state.userId = truncate(userId, 64);
		state.internal = internal;
		if (changed) {
			enqueue('$identify');
		}
	}),

	// Explicit logout only (Logout menu item, /sign-out), not a stale token
	// being cleared at page load: end the session so the next one isn't
	// attributed to this user
	reset: safe(() => {
		if (!state) {
			return;
		}
		void state.transport.flush();
		state.replay?.stop();
		state.userId = undefined;
		state.internal = false;
		endSession();
	}),

	// Opt-out: stop everything and drop anything not yet sent
	stop: safe(() => {
		if (!state) {
			return;
		}
		state.capture.stop();
		state.transport.clear();
		state.replay?.discard();
		document.removeEventListener('visibilitychange', onHide);
		window.removeEventListener('pagehide', onHide);
		state = null;
	}),
};
